import React, { useState, useMemo } from 'react';
import { Container, Paper, Text, Group, Stack, Box, SimpleGrid, Button, Modal, Image, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconUser, IconCalendar, IconUsers, IconMapPin, IconEye, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { showNotification, Notifications } from '@mantine/notifications';

interface Subscription {
  id: number;
  subscribable_type: 'Artist' | 'Organizer' | 'Area';
  subscribable_id: number;
  created_at: string;
  name: string;
  username?: string;
  slug?: string;
}

interface Props {
  subscriptions: Subscription[];
}

const ManageSubscriptions: React.FC<Props> = ({ subscriptions }) => {
  const [selected, setSelected] = useState<Subscription | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [subscriptionsState, setSubscriptionsState] = useState(subscriptions);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const summary = useMemo(() => {
    return subscriptionsState.reduce(
      (acc, sub) => {
        acc[sub.subscribable_type]++;
        return acc;
      },
      { Artist: 0, Organizer: 0, Area: 0 },
    );
  }, [subscriptionsState]);

  const handleUnsubscribe = async () => {
    if (!selected) return;

    try {
      const response = await fetch(`/subscriptions/${selected.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });

      const data = await response.json();

      if (response.ok) {
        showNotification({
          title: 'Unsubscribed',
          message: `You have unsubscribed from ${selected.name}.`,
          color: 'green',
          position: 'top-right',
        });
        setSubscriptionsState((prev) => prev.filter((s) => s.id !== selected.id));
        setConfirmOpen(false);
        setSelected(null);
      } else {
        showNotification({
          title: 'Error',
          message: data.message || 'Unsubscribe failed.',
          color: 'red',
          position: 'top-right',
        });
      }
    } catch (error: any) {
      showNotification({
        title: 'Error',
        message: error.message || 'Something went wrong.',
        color: 'red',
        position: 'top-right',
      });
    }
  };

  const grouped = useMemo(() => {
    return subscriptionsState.reduce(
      (acc, sub) => {
        if (!acc[sub.subscribable_type]) acc[sub.subscribable_type] = [];
        acc[sub.subscribable_type].push(sub);
        return acc;
      },
      {} as Record<'Artist' | 'Organizer' | 'Area', Subscription[]>,
    );
  }, [subscriptionsState]);

  const StatCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) => (
    <Paper p="md" radius="lg" style={{ backgroundColor: '#17171790', textAlign: 'center' }}>
      <Stack gap="4px" align="center">
        <Text size="2rem" fw={700} c="white" lh={1}>
          {value}
        </Text>
        <Group gap="4px" justify="center">
          {icon}
          <Text size="12px" c="dimmed">
            {label}
          </Text>
        </Group>
      </Stack>
    </Paper>
  );

  console.log(
    '📦 subscription types:',
    subscriptions.map((s) => s.subscribable_type),
  );

  return (
    <Container p="lg" style={{ marginBottom: '1rem', backgroundColor: '#75757590', borderRadius: '8px' }}>
      <Notifications />
      <Text fw={700} size="2rem" c="white" mb="md" className="rubik-header mb-4">
        Your Subscriptions
      </Text>

      <SimpleGrid cols={isMobile ? 1 : 3} spacing="xs" mb="2rem">
        <StatCard
          icon={<IconUser size={20} color="#666" />}
          label={summary.Artist === 1 ? 'Artist' : 'Artists'}
          value={summary.Artist}
        />
        <StatCard
          icon={<IconUsers size={20} color="#666" />}
          label={summary.Organizer === 1 ? 'Organizer' : 'Organizers'}
          value={summary.Organizer}
        />
        <StatCard
          icon={<IconMapPin size={20} color="#666" />}
          label={summary.Area === 1 ? 'Area' : 'Areas'}
          value={summary.Area}
        />
      </SimpleGrid>

      <Stack>
        {subscriptions.length > 0 ? (
          (['Artist', 'Organizer', 'Area'] as const).map((type) => {
            const group = grouped[type];
            if (!group || group.length === 0) return null;

            return (
              <Box key={type}>
                <Text fw={700} c="white" size="lg" className="my-2">
                  {type}s
                </Text>
                <Stack gap="8px">
                  {group.map((sub) => (
                    <Paper key={sub.id} shadow="xs" radius="md" p="sm" style={{ backgroundColor: '#17171790' }}>
                      <Group justify="space-between" align="right">
                        <Box>
                          <Text size="1rem" fw={500} c="white" className="mb-1">
                            {sub.name}
                          </Text>
                          <Text size="12px" c="dimmed">
                            Subscribed on {dayjs(sub.created_at).format('MMM D, YYYY')}
                          </Text>
                        </Box>
                        <Box w="100%" style={{ textAlign: 'right' }}>
                          {/* this inline-block span holds both buttons together */}
                          <Box
                            component="span"
                            style={{
                              display: 'inline-block',
                              whiteSpace: 'nowrap', // no wrapping inside here
                            }}
                          >
                            <Button
                              variant="light"
                              color="orange"
                              size="xs"
                              component="a"
                              leftSection={<IconEye size={16} />}
                              href={sub.url}
                              className="mr-2"
                            >
                              View
                            </Button>
                            <Button
                              variant="light"
                              color="red"
                              size="xs"
                              leftSection={<IconX size={16} />}
                              onClick={() => {
                                setSelected(sub);
                                setConfirmOpen(true);
                              }}
                            >
                              Unsubscribe
                            </Button>
                          </Box>
                        </Box>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            );
          })
        ) : (
          <Paper p="md" radius="md" style={{ backgroundColor: '#171717', textAlign: 'left' }}>
            <Text c="white" fw={500}>
              You haven’t subscribed to anyone yet.
            </Text>
            <Text size="sm" c="dimmed" mt="xs">
              Check out any artist, organizer, or area pages to create your first subscription!
            </Text>
          </Paper>
        )}
      </Stack>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Unsubscribe"
        overlayProps={{ opacity: 0.55, blur: 4 }}
      >
        <Text className="my-3">
          Are you sure you want to unsubscribe from <strong>{selected?.name}</strong>?
        </Text>
        <Group justify="flex-end" align="center">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleUnsubscribe}>
            Unsubscribe
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default ManageSubscriptions;
